"use client";
import ModalUpcoming from "@/app/[locale]/(user-page)/report/components/Modal/ModalUpcoming";
import ThemeRegistry from "@/components/Theme/ThemeRegistry";
import image from "@/constants/image";
import { APP_ROUTES } from "@/constants/routes";
import UseAuth from "@/hooks/useAuth";
import { paramsReport } from "@/models/Params";
import { TYPE_FAVORITE } from "@/models/report";
import { User } from "@/models/user";
import ReportServices from "@/services/main/reportServices";
import { roundNumberToK } from "@/utils";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Box, Grid2, Tooltip, Typography } from "@mui/material";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Montserrat } from "next/font/google";
import Image from "next/image";
import { redirect } from "next/navigation";
import React, { useState } from "react";

interface UpcomingProps {
  queryKey?: string;
  locale: string;
}

const montserrat = Montserrat({
  subsets: ["latin"],
});

const UpcomingSoon = (props: UpcomingProps) => {
  const queryClient = useQueryClient();
  const user = queryClient.getQueryData<User>(["user"]);
  const isLoggedIn = !!user;
  const { queryKey, locale } = props;
  const t = useTranslations("messages");
  const dataSelectRecently = [
    { name: t("common_newest_first"), isDescending: true },
    { name: t("common_oldest_first"), isDescending: false },
  ];
  const [select, setSelect] = useState(dataSelectRecently[0]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openModal, setOpenModal] = useState(false);
  const {} = UseAuth();
  const [isDescending, setIsDescending] = useState(true);
  const take = 12;

  const {
    data: comingSoonData,
    isLoading,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [queryKey, isDescending],
    queryFn: async ({ pageParam }: { pageParam?: number | string }) => {
      const params: paramsReport = {
        take: take,
        page: pageParam as any,
        isDescending: isDescending,
        sortedField: "id",
        lang: locale,
      };
      const result = await ReportServices.getAllReportCommingSoon(params);
      return result;
    },
    placeholderData: (prev: any) => prev,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.hasMore) {
        return lastPage.meta.page + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      return firstPage.meta.page > 1 ? firstPage.meta.page - 1 : undefined;
    },
    select: (data: any) => ({
      data: (data.pages as any).flatMap((item: any): Report[] => item.data),
      hasMore: (data.pages as any).flatMap((item: any) => item.meta.hasMore),
    }),
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const [dataModalUpcoming, setDataModalUpcoming] = useState(null);

  const AddFavorite = useMutation({
    mutationFn: async (reportId: number) => {
      const result = await ReportServices.postAddFavorite(
        reportId,
        TYPE_FAVORITE.FAVORITE
      );
      return result;
    },
  });

  const handleClickOpenModal = (item: any) => {
    setDataModalUpcoming(item);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleChange = (value: any) => {
    setIsDescending(value.isDescending);
    setSelect(value);
    handleClose();
  };

  const handleFavorite = async (
    id: number,
    currentFavorites: boolean,
    currentCount: number
  ) => {
    if (!isLoggedIn) {
      const currentPath =
        typeof window !== "undefined"
          ? window.location.pathname +
            window.location.search +
            window.location.hash
          : "";
      redirect(
        `${APP_ROUTES(locale as "en" | "vi" | "id").LOGIN.to}?redirect=${encodeURIComponent(currentPath)}`
      );
      return;
    }
    const isCurrentlyFavorited = currentFavorites;
    const newFavorites = isCurrentlyFavorited ? false : true;
    const newCount = isCurrentlyFavorited
      ? Number(currentCount) - 1
      : Number(currentCount) + 1;
    queryClient.setQueryData([queryKey, isDescending], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          data: page.data.map((item: any) =>
            item.id === id
              ? {
                  ...item,
                  isFavorite: newFavorites,
                  favoritesCount: newCount,
                }
              : item
          ),
        })),
      };
    });
    try {
      await AddFavorite.mutateAsync(id);
    } catch {
      queryClient.setQueryData([queryKey, isDescending], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: page.data.map((item: any) =>
              item.id === id
                ? {
                    ...item,
                    isFavorite: currentFavorites,
                    favoritesCount: currentCount,
                  }
                : item
            ),
          })),
        };
      });
    }
  };
  return (
    <ThemeRegistry>
      <Box className="px-5 md:px-10 xl:px-[100px] my-10">
        <Box className="sm:flex justify-between items-center py-4">
          <Box className="flex items-center gap-2">
            <Image
              src={image.iconCampaign}
              alt="iconCampaign"
              className="!h-8 w-8"
              style={{
                objectFit: "contain",
                width: "auto",
                height: "auto",
              }}
            />
            <h1
              className="text-eerie_black font-semibold text-3xl/6 leading-8 sm:text-[32px]/[39.01px] m-0"
              id="UpcomingReports"
              translation-key="upcoming_reports"
            >
              {t("upcoming_reports")}{" "}
              {!isLoggedIn ? "" : t("home_upcoming_reports_cast_your_vote")}
            </h1>
          </Box>
          {/* <Box className="flex justify-end mt-5 sm:mt-0">
            <Button
              variant="outlined"
              className={`!py-2 !px-4 gap-[2.47px] grid h-10 rounded border-1 !border-gray_20`}
              onClick={handleClick}
            >
              <p
                className={`text-base/6 font-normal normal-case text-gray_90 m-0`}
              >
                {select.name}
              </p>
              <KeyboardArrowDownIcon
                className={`text-gray_60 ${anchorEl && "rotate-180"}`}
              />
            </Button>
            <Popover
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              sx={{
                ".MuiPaper-root": {
                  padding: "8px 0px",
                },
              }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    boxShadow: "0px 0px 8px 0px rgba(28, 28, 28, 0.12)",
                    backgroundColor: "rgba(255, 255, 255, 1)",
                  },
                },
              }}
              transformOrigin={{
                horizontal: "center",
                vertical: "top",
              }}
              anchorOrigin={{
                horizontal: "center",
                vertical: "bottom",
              }}
            >
              {dataSelectRecently.map((item, index) => (
                <MenuItem
                  key={index}
                  className={` px-4 !py-2 gap-[16.42px] !text-sm/6 !font-normal normal-case ${
                    item?.name === select.name
                      ? "!bg-cimigo_blue_light_1 !text-white hover:!bg-ciBlue_dark_1 flex justify-between"
                      : "text-gray_80"
                  }`}
                  onClick={() => handleChange(item)}
                >
                  {item?.name}
                  {item?.name === select.name && (
                    <DoneIcon className="w-5 h-5" />
                  )}
                </MenuItem>
              ))}
            </Popover>
          </Box> */}
        </Box>
        <Box className="mt-2">
          <Grid2 container columnSpacing={4} rowSpacing={4} marginBottom={4}>
            {comingSoonData?.data?.length > 0 ? (
              comingSoonData?.data?.map((item: any, index: any) => (
                <Grid2
                  size={{ xs: 12, md: 6, lg: 4 }}
                  key={index}
                  className="rounded-lg p-6 border border-[#E8E8E8] shadow-[0px_1px_6px_rgba(0,0,0,0.15)] hover:cursor-pointer"
                  onClick={() => handleClickOpenModal(item)}
                >
                  <motion.div className="grid gap-6 group">
                    <Box
                      // onClick={() => handleClickOpenModal(item)}
                      className="overflow-hidden"
                    >
                      <p className="font-semibold text-base/6 text-eerie_black h-12 group-hover:text-ciBlue_900 m-0 truncate">
                        {item?.title}
                      </p>
                    </Box>
                    <Box className="flex items-center  justify-between">
                      <Box className="overflow-hidden">
                        <p
                          className={`text-[#494949] font-medium text-sm/[17.07px] m-0 ${isLoggedIn ? "max-w-[230px]" : "max-w-[350px]"} truncate`}
                        >
                          {item?.categories.map(
                            (itemCategory: any, index: any) =>
                              itemCategory?.name +
                              `${item?.categories.length - 1 > index ? ", " : ""}`
                          )}
                        </p>
                      </Box>
                      {isLoggedIn && (
                        <Box className="flex items-center">
                          <Typography className=" !text-gray-600 !font-medium !text-sm !mr-4 sm:!mr-[5px]">
                            {roundNumberToK(item?.favoritesCount || 0)}
                          </Typography>
                          <Tooltip
                            title={
                              <span
                                translation-key={
                                  item?.isFavorite
                                    ? t("common_favorited")
                                    : t("common_favorite")
                                }
                                className={`${montserrat.className}`}
                              >
                                {item?.isFavorite
                                  ? t("common_favorited")
                                  : t("common_favorite")}
                              </span>
                            }
                            placement="bottom"
                            slotProps={{
                              popper: {
                                modifiers: [
                                  {
                                    name: "offset",
                                    options: {
                                      offset: [0, -10], // Điều chỉnh vị trí của tooltip
                                    },
                                  },
                                ],
                              },
                            }}
                          >
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              className="flex items-center justify-center border-0 py-[1px] px-1 bg-white hover:bg-white cursor-pointer rounded"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                handleFavorite(
                                  item?.id,
                                  item?.isFavorite,
                                  item?.favoritesCount
                                );
                              }}
                            >
                              {item?.isFavorite ? (
                                <FavoriteIcon className="h-6 w-6 text-warning" />
                              ) : (
                                <FavoriteBorderIcon className="h-6 w-6 text-warning" />
                              )}
                            </motion.button>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </motion.div>
                </Grid2>
              ))
            ) : (
              <Box className="w-full flex justify-center items-center">
                <p className="text-gray_80" translation-key="common_no_reports">
                  {t("common_no_reports")}
                </p>
              </Box>
            )}
          </Grid2>
          {comingSoonData?.data?.length >= take &&
            comingSoonData?.hasMore[comingSoonData?.hasMore?.length - 1] && (
              <Box
                className="py-2 gap-4 bg-ciBlue_1 rounded hover:cursor-pointer hover:bg-ciBlue_2"
                onClick={() => fetchNextPage()}
              >
                <Box className="py-2 px-4 gap-2 flex justify-center">
                  <p
                    className="font-medium text-base/6 text-ciBlue_900 m-0"
                    translation-key="common_show_more_upcoming"
                  >
                    {t("common_show_more_upcoming")}
                  </p>
                </Box>
              </Box>
            )}
        </Box>
      </Box>
      <ModalUpcoming
        open={openModal}
        handleClose={handleCloseModal}
        isLoggedIn={isLoggedIn}
        dataModalUpcoming={dataModalUpcoming}
        queryKeyUpcoming={[queryKey, isDescending]}
      />
    </ThemeRegistry>
  );
};

export default UpcomingSoon;
